<script setup>
// =============================================================================
// FangateSidebar.vue — Stage C: sidebar navigation
// Mirrors prototype lines 1271–1451 + interactive JS behaviour.
// =============================================================================

import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import FangateAPI from 'dashboard/api/fangate';
import SearchAPI from 'dashboard/api/search';
// `isAdmin` already exists below as a demo-role toggle (props.role === 'admin').
// For the real security gate on the AGENTS section we need the ACTUAL Chatwoot
// user role, not the demo dropdown — aliased here as isAccountAdmin so the two
// stay distinct.
import { useAdmin } from 'dashboard/composables/useAdmin';
const { isAdmin: isAccountAdmin } = useAdmin();
const store = useStore();

// ---- Props / Emits ----------------------------------------------------------
const props = defineProps({
  /** All inboxes from Vuex (passed down from FangateLayout) */
  inboxes: { type: Array, default: () => [] },
  /** Map { [inboxId]: unread } — DM/chat-only unread, drives the CHATS pills + sort */
  unreadByInbox: { type: Object, default: () => ({}) },
  /** Map { [inboxId]: unread } — group/supergroup/channel unread, drives CHANNELS sort */
  unreadByInboxChannels: { type: Object, default: () => ({}) },
  /** Map { [inboxId]: total } — DM/chat-only total */
  totalByInbox:  { type: Object, default: () => ({}) },
  /** Map { [inboxId]: total } — group/supergroup/channel total */
  totalByInboxChannels: { type: Object, default: () => ({}) },
  /** Map { [inboxId]: unix-seconds } — most-recent conversation activity per
   *  inbox. Drives the activity-based sort so just-touched accounts pop up. */
  lastActivityByInbox: { type: Object, default: () => ({}) },
  /** Currently selected inbox id */
  selectedInboxId: { type: Number, default: null },
  /** Currently active Fangate vault inbox id (drives the FANGATE list highlight) */
  selectedFangateInboxId: { type: Number, default: null },
  /** Which view the list pane is showing — 'chats' | 'channels' | 'agents' */
  view: { type: String, default: 'chats' },
  /** Agents list — drives the "Filter by agent" section in the filter dropdown */
  agents: { type: Array, default: () => [] },
  /** Map inboxId -> [agentId]: who's assigned to each inbox (for the agent filter) */
  inboxAgentMap: { type: Object, default: () => ({}) },
  /** All labels from Vuex — drives the LABELS section (cross-account label view) */
  labels: { type: Array, default: () => [] },
  /** Currently active label title (drives the LABELS list highlight) */
  selectedLabel: { type: String, default: null },
});
const emit = defineEmits([
  'select-inbox',
  'select-chat-account',
  'select-channel-account',
  'select-fangate-account',
  'select-reports-account',
  'select-view',
  'open-agent-picker',
  'select-search-conversation',
  'select-label',
]);

// ---- Section open/closed ----------------------------------------------------
const open = reactive({ chats: true, channels: false, labels: false, fangate: true, reports: false, settings: false });
const toggleSection = key => { open[key] = !open[key]; };

// ---- Sub-group collapsed ----------------------------------------------------
const subOpen = reactive({ 'chats-unread': true, 'chats-all': true });
const toggleSub = key => { subOpen[key] = !subOpen[key]; };

// LABELS section → switch the list pane to a cross-account view of every chat
// (across all readable accounts) carrying this label. Parent fetches them
// server-side, so the ACL (agents only see their inboxes) is respected.
const selectLabel = title => emit('select-label', title);

// ---- Active item ------------------------------------------------------------
// Persisted to localStorage so the green highlight survives F5 / browser
// restart. The active flag in template is derived from the FULL key
// (chats-unread-{accountKey} vs chats-account-{accountKey}) — that's how
// we tell apart "user clicked it in UNREAD" vs "user clicked it in ALL"
// and only light up the row they actually used.
const ACTIVE_ITEM_KEY = 'fangate_sidebar_active_item';
const activeItem = ref(null);
try { activeItem.value = localStorage.getItem(ACTIVE_ITEM_KEY) || null; } catch (_) {}
const persistActiveItem = v => {
  try {
    if (v) localStorage.setItem(ACTIVE_ITEM_KEY, v);
    else   localStorage.removeItem(ACTIVE_ITEM_KEY);
  } catch (_) {}
};
const setActive = (item, inboxId) => {
  activeItem.value = item;
  persistActiveItem(item);
  if (inboxId) emit('select-inbox', inboxId);
};

// CHATS section header click → switch the list pane to the multi-account
// overview (no specific inbox selected). Drops `activeItem` so the
// previously-highlighted account loses its active-state pill, and emits
// `select-view: 'chats'` which routes to `handleSelectView('chats')` in
// FangateLayout — that handler already clears selectedInboxId + the
// active conversation, snapping FangateListPane back to `#layer-accounts`.
const openAccountsOverview = () => {
  activeItem.value = null;
  persistActiveItem(null);
  emit('select-view', 'chats');
};
// Channels & Groups section header click — same overview-opening pattern as
// CHATS, but emits `select-view: 'channels'` so FangateLayout's
// handleSelectView clears the active conversation/inbox and the list pane
// snaps back to the multi-account overview rendered for the channels view.
const openChannelsOverview = () => {
  activeItem.value = null;
  persistActiveItem(null);
  emit('select-view', 'channels');
};
// Labels section header click — opens a labels directory in the list pane
// (parallels the CHATS accounts overview). Clicking a label there drills into
// the cross-account label view (view === 'label').
const openLabelsOverview = () => {
  activeItem.value = null;
  persistActiveItem(null);
  emit('select-view', 'labels-overview');
};
// Chats nav: clicking a Telegram account → list pane FORCED into chats view.
// Mirror of setChannelAccount below: uses a dedicated emit (select-chat-account)
// so FangateLayout's handleSelectChatAccount sets listView='chats' explicitly
// instead of going through the view-preserving handleSelectInbox path (which
// would leave the user in 'channels' if that was the previous view).
const setChatAccount = (acc) => {
  const key = 'chats-account-' + acc.key;
  activeItem.value = key;
  persistActiveItem(key);
  emit('select-chat-account', acc.id);
};
// Channels nav: clicking a Telegram account → list pane shows channels for it
const setChannelAccount = (acc) => {
  const key = 'channel-account-' + acc.key;
  activeItem.value = key;
  persistActiveItem(key);
  emit('select-channel-account', acc.id);
};

// ============================================================================
// Filter / Sort dropdowns (CHATS section)
// ============================================================================

// Four account menus across the CHATS + CHANNELS sections. Only ONE may be
// open at a time (filter OR sort, chats OR channels) — every toggle closes
// the other three (one-popup rule).
const showFilterMenu   = ref(false);  // chats filter
const showSortMenu     = ref(false);  // chats sort
const showFilterMenuCh = ref(false);  // channels filter
const showSortMenuCh   = ref(false);  // channels sort

const closeAccountMenus = except => {
  if (except !== 'filter')   showFilterMenu.value   = false;
  if (except !== 'sort')     showSortMenu.value     = false;
  if (except !== 'filterCh') showFilterMenuCh.value = false;
  if (except !== 'sortCh')   showSortMenuCh.value   = false;
};
const toggleFilter = e => {
  e.stopPropagation();
  const next = !showFilterMenu.value;
  closeAccountMenus('filter');
  showFilterMenu.value = next;
};
const toggleSort = e => {
  e.stopPropagation();
  const next = !showSortMenu.value;
  closeAccountMenus('sort');
  showSortMenu.value = next;
};
const toggleFilterCh = e => {
  e.stopPropagation();
  const next = !showFilterMenuCh.value;
  closeAccountMenus('filterCh');
  showFilterMenuCh.value = next;
};
const toggleSortCh = e => {
  e.stopPropagation();
  const next = !showSortMenuCh.value;
  closeAccountMenus('sortCh');
  showSortMenuCh.value = next;
};

// ---- Real inbox data (from props) -------------------------------------------
// Which inboxes are hidden via the filter menu
const hiddenAccounts = reactive(new Set());
const toggleAccountVisibility = key => {
  if (hiddenAccounts.has(key)) hiddenAccounts.delete(key);
  else hiddenAccounts.add(key);
};
const isHidden   = key => hiddenAccounts.has(key);
const filterActive = computed(() => hiddenAccounts.size > 0 || sbActiveAgents.size > 0 || sbActiveLabels.size > 0);

// Sort
const SB_SORT_OPTIONS = [
  // 'Activity' is the new default — the sort now actively floats unread +
  // most-recently-touched inboxes to the top (#157). 'default' as the key
  // stays for backward compatibility with persisted UI state.
  { id: 'default', label: 'Activity' },
  { id: 'alpha',   label: 'Alphabetical' },
];
const sbAccountSort = ref('default');
const setSbSort = id => {
  sbAccountSort.value = id;
  showSortMenu.value  = false;
};
const sortActive = computed(() => sbAccountSort.value !== 'default');

// Map inboxes → sidebar-friendly shape.
// Unread counts come from `unreadByInbox` (computed in FangateLayout from a
// global open-conversations fetch). Falls back to 0 until that lands.
// `unread` is DM-only for the CHATS section; `unreadChannels` is the
// group/supergroup/channel-only count for the CHANNELS section. Each section
// sorts on its own unread band, so a group ping never bubbles an account up
// in CHATS and a DM never bubbles it up in CHANNELS.
const inboxItems = computed(() =>
  props.inboxes.map(inbox => ({
    key:             String(inbox.id),
    id:              inbox.id,
    name:            inbox.name,
    avatar:          inbox.avatar_url || '',
    unread:          props.unreadByInbox[inbox.id]          || 0,
    unreadChannels:  props.unreadByInboxChannels[inbox.id]  || 0,
    total:           props.totalByInbox[inbox.id]           || 0,
    totalChannels:   props.totalByInboxChannels[inbox.id]   || 0,
    lastActivity:    props.lastActivityByInbox[inbox.id]    || 0,
  }))
);

// Filter the ACCOUNT list by assigned agent (inbox membership). Empty Set =
// no filter. With one+ agents selected, only inboxes where at least one of
// them is a member stay visible.
const sbActiveAgents = reactive(new Set());
const toggleSbAgent = agentId => {
  if (sbActiveAgents.has(agentId)) sbActiveAgents.delete(agentId);
  else sbActiveAgents.add(agentId);
};
const accountMatchesAgentFilter = item => {
  if (sbActiveAgents.size === 0) return true;
  const ags = props.inboxAgentMap[item.id] || [];
  return [...sbActiveAgents].some(id => ags.includes(id));
};

// ---- Filter accounts by label ----------------------------------------------
// Shows only accounts that have ≥1 conversation carrying a selected label.
// Approximate by design (user-accepted): only conversations currently in the
// Vuex store count — so an account whose chats haven't been loaded yet won't
// match until its chats are fetched. The cross-account "Labels" section is the
// exhaustive, server-fetched lens; this is the lightweight account filter.
const sbActiveLabels = reactive(new Set());
const toggleSbLabel = title => {
  if (sbActiveLabels.has(title)) sbActiveLabels.delete(title);
  else sbActiveLabels.add(title);
};
const inboxIdsWithActiveLabels = computed(() => {
  const set = new Set();
  if (!sbActiveLabels.size) return set;
  const convs = store.getters.getAllConversations || [];
  for (const c of convs) {
    const ls = c?.labels || [];
    if (c.inbox_id != null && ls.some(l => sbActiveLabels.has(l))) {
      set.add(Number(c.inbox_id));
    }
  }
  return set;
});
const accountMatchesLabelFilter = item =>
  sbActiveLabels.size === 0 || inboxIdsWithActiveLabels.value.has(Number(item.id));

// Initials fallback for avatars
const getInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
};

// ----------------------------------------------------------------------------
// Settings sub-item navigation — INLINE (no new tab, no route change)
//
// We emit `select-view` with the settings-view id; FangateLayout maps these
// to its Col 3 view-state and renders FangateSettings instead of FangateChatArea.
// The Fangate shell (sidebar, topbar, panel) stays mounted throughout.
// ----------------------------------------------------------------------------
const goToSettings = which => {
  const viewMap = {
    inboxes:   'settings-inboxes',
    bots:      'settings-bots',
    labels:    'settings-labels',
    account:   'settings-account',
    help:      'settings-help',
  };
  const view = viewMap[which];
  if (view) {
    activeItem.value = view;
    persistActiveItem(view);
    emit('select-view', view);
  }
};

// Visible + sorted account list (drives the sidebar items). Updated rule
// per user: the order should ONLY shift when an incoming unread message
// arrives. Outgoing sends, polls, focus refreshes — none of those move
// rows around any more.
//
// Default sort:
//   1. accounts with unread (unread > 0) above accounts without
//   2. within the UNREAD band: most-recent activity DESC (so the freshest
//      unread sits at the very top — the urgent one)
//   3. within the READ band: alphabetical — STABLE order, no movement
//      when you send messages or browse around
// The legacy 'alpha' sort menu option still works as an explicit override.
// Sort helper — same rule for both sections, parameterized on which unread
// field drives the band. unreadKey ∈ { 'unread', 'unreadChannels' }.
const sortByUnreadBand = (list, unreadKey) => {
  if (sbAccountSort.value === 'alpha') {
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }
  // 'Activity' sort: newest activity on top, REGARDLESS of read state. Reading a
  // conversation doesn't change its account's lastActivity, so a just-opened
  // account stays put — it only drops when ANOTHER account gets newer activity
  // (a new inbound/outbound message). Previously this used an unread-first band
  // and sorted read accounts alphabetically, which yanked an account back down
  // the moment you clicked its (now-read) message. Unread is only a tie-breaker
  // now (edges out a read account when their activity timestamps are equal).
  return [...list].sort((a, b) => {
    const actDiff = (b.lastActivity || 0) - (a.lastActivity || 0);
    if (actDiff !== 0) return actDiff;
    const aHasUnread = a[unreadKey] > 0 ? 1 : 0;
    const bHasUnread = b[unreadKey] > 0 ? 1 : 0;
    if (aHasUnread !== bHasUnread) return bHasUnread - aHasUnread;
    return a.name.localeCompare(b.name);
  });
};
// Sidebar search — the top "Search…" box live-filters the account lists by name
// (client-side, instant). Empty query = no filtering.
const sbSearch = ref('');
const matchesSearch = acc => {
  const q = sbSearch.value.trim().toLowerCase();
  return !q || (acc.name || '').toLowerCase().includes(q);
};

// ---- Global search: Chats + message CONTENT (server full-text) -------------
// Accounts stay filtered in-place above. Conversations + messages come from
// Chatwoot's search API (debounced) and render in a dropdown under the box;
// clicking a hit opens that conversation IN OUR SHELL (not the stock router).
const searchConversations = ref([]);
const searchMessages = ref([]);
const searchLoading = ref(false);
const searchDropdownOpen = ref(false);
let searchTimer = null;
let searchSeq = 0;

const runGlobalSearch = async q => {
  const seq = ++searchSeq;
  searchLoading.value = true;
  searchDropdownOpen.value = true;
  try {
    // Only the two entities the dropdown actually renders. The combined /search
    // (all) endpoint also runs contacts + articles queries on every keystroke,
    // which we never display → pure wasted DB work. Fetch the two we need in
    // parallel instead. (Searching a contact name still finds the chat: that's
    // handled by the conversations query via its contacts JOIN, not /contacts.)
    const [convRes, msgRes] = await Promise.all([
      SearchAPI.conversations({ q }),
      SearchAPI.messages({ q }),
    ]);
    if (seq !== searchSeq) return; // a newer query superseded this one
    searchConversations.value = (convRes?.data?.payload?.conversations || []).slice(0, 8);
    searchMessages.value = (msgRes?.data?.payload?.messages || []).slice(0, 8);
  } catch (_) {
    if (seq !== searchSeq) return;
    searchConversations.value = [];
    searchMessages.value = [];
  } finally {
    if (seq === searchSeq) searchLoading.value = false;
  }
};

watch(sbSearch, val => {
  const q = (val || '').trim();
  if (searchTimer) clearTimeout(searchTimer);
  // Min 3 chars: the messages.content / contacts trigram GIN indexes need 3-char
  // trigrams to be used efficiently; 2-char queries fall back to a slow scan.
  if (q.length < 3) {
    searchConversations.value = [];
    searchMessages.value = [];
    searchDropdownOpen.value = false;
    searchLoading.value = false;
    return;
  }
  searchTimer = setTimeout(() => runGlobalSearch(q), 300);
});

const searchSnippet = txt => {
  const s = (txt || '').replace(/\s+/g, ' ').trim();
  return s.length > 70 ? `${s.slice(0, 70)}…` : s;
};

const clearGlobalSearch = () => {
  sbSearch.value = '';
  searchConversations.value = [];
  searchMessages.value = [];
  searchDropdownOpen.value = false;
};

const reopenSearchIfResults = () => {
  if (sbSearch.value.trim().length >= 2 &&
      (searchConversations.value.length || searchMessages.value.length)) {
    searchDropdownOpen.value = true;
  }
};

// Open a conversation hit in our shell — normalise to the shape
// FangateLayout.handleSelectConversation expects (id = display_id + inbox_id).
const openSearchConversation = conv => {
  emit('select-search-conversation', {
    id: conv.id,
    inbox_id: conv.inbox?.id ?? conv.inboxId ?? null,
    meta: { sender: conv.contact || {} },
  });
  clearGlobalSearch();
};

// Open the conversation a message hit belongs to.
const openSearchMessage = msg => {
  // Search-message results are snake_case from the API serializer
  // (_message.json.jbuilder): `conversation_id` is the conversation DISPLAY id,
  // `inbox_id` is the inbox FK. (The ES path nests conversation:{id:display_id} —
  // prefer that when present.) NOT camelCase — reading msg.conversationId here
  // returned undefined and silently broke every message-hit click.
  const convId = msg.conversation?.id ?? msg.conversation_id;
  emit('select-search-conversation', {
    id: convId,
    inbox_id: msg.inbox_id ?? null,
    meta: { sender: msg.sender || {} },
    // The specific message hit → ChatArea scrolls to + flashes it.
    messageId: msg.id,
  });
  clearGlobalSearch();
};

const visibleAccounts = computed(() =>
  sortByUnreadBand(inboxItems.value.filter(a => !hiddenAccounts.has(a.key) && accountMatchesAgentFilter(a) && accountMatchesLabelFilter(a) && matchesSearch(a)), 'unread')
);
// Independent sort for the CHANNELS section so a group ping there doesn't
// shuffle the CHATS section.
const visibleAccountsChannels = computed(() =>
  sortByUnreadBand(inboxItems.value.filter(a => !hiddenAccounts.has(a.key) && accountMatchesAgentFilter(a) && accountMatchesLabelFilter(a) && matchesSearch(a)), 'unreadChannels')
);
// Section-header badges (top of the unread hierarchy): how many ACCOUNTS
// currently have unread messages — DM/chat unread for CHATS, group/channel
// unread for CHANNELS. One level above the per-account badge (= how many chats
// are unread), which is itself above the per-chat badge (= unread msg count).
const unreadAccounts = computed(() => visibleAccounts.value.filter(a => a.unread > 0));
const unreadAccountsChannels = computed(() => visibleAccountsChannels.value.filter(a => a.unreadChannels > 0));

// Filter menu: show all inboxes (including hidden)
const allInboxItems = computed(() => inboxItems.value);

// Nav-item right-click → open shared agent picker in parent (FangateLayout)
const onNavItemRightClick = (e, acc) => {
  e.preventDefault();
  e.stopPropagation();
  const inbox = props.inboxes.find(i => i.id === acc.id) ?? { id: acc.id, name: acc.name };
  emit('open-agent-picker', { event: e, inbox });
};

// ============================================================================
// Fangate accounts — inbox list filtered to those with a Fangate token
// configured. The token lives per-inbox in localStorage under
// `fangate_token_<id>` (see dashboard/api/fangate.js). Chatwoot's inbox ACL
// has already filtered props.inboxes to what the current user can see, so
// agents naturally only see the Fangate accounts they're assigned to.
//
// Each entry shape: { id, key, name, avatar }
//   - id     : the inbox id (used by FangateAPI when scoping)
//   - key    : string id for v-for key uniqueness
//   - name   : display name (inbox.name)
//   - avatar : inbox.avatar_url or empty (initials fallback handled below)
// ============================================================================
// localStorage isn't reactive — a token paste in Settings wouldn't refresh
// fgAccounts on its own. Bump this counter on a window 'storage' event
// (any tab can trigger it) and on focus, so the sidebar re-evaluates
// without needing a full page reload.
const fgConnectionTick = ref(0);
const onStorageChange = e => {
  if (!e || !e.key || e.key.startsWith('fangate_token_')) fgConnectionTick.value++;
};
const onWindowFocus = () => { fgConnectionTick.value++; };
onMounted(() => {
  window.addEventListener('storage', onStorageChange);
  window.addEventListener('focus', onWindowFocus);
});
onUnmounted(() => {
  window.removeEventListener('storage', onStorageChange);
  window.removeEventListener('focus', onWindowFocus);
});

// Pull each inbox's durable token from the server into the localStorage cache
// once the inbox list is available (Task #91). This is what makes the Fangate
// link survive a browser-cache-clear or a device switch — the sidebar's
// connection state recovers from the server instead of looking "disconnected".
// Bump the tick afterwards so fgAccounts re-evaluates with the hydrated state.
watch(
  () => (props.inboxes || []).map(i => i.id).join(','),
  () => {
    const ids = (props.inboxes || []).map(i => i.id).filter(Boolean);
    if (ids.length) FangateAPI.hydrateInboxes(ids).then(() => { fgConnectionTick.value += 1; });
  },
  { immediate: true }
);

const fgAccounts = computed(() => {
  // Include the tick in the computed deps so storage / focus events
  // force a re-evaluation. Reading the ref makes Vue track it.
  void fgConnectionTick.value;
  return (props.inboxes || [])
    .filter(inbox => FangateAPI.isInboxConnected(inbox.id))
    .map(inbox => ({
      id:     inbox.id,
      key:    String(inbox.id),
      name:   inbox.name || `Inbox #${inbox.id}`,
      avatar: inbox.avatar_url || '',
    }))
    .filter(matchesSearch)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
});

const selectFgAccount = acc => {
  // Bubble up so FangateLayout can switch to the fullscreen vault view.
  // The "currently selected" highlight is driven from the parent via the
  // selectedFangateInboxId prop, so we don't keep local state here.
  emit('select-fangate-account', acc);
};
// Reports → open the "Transactions & Live analytics" panel for this account.
// Parent swaps to the reports-transactions view; highlight reuses the same
// selectedFangateInboxId prop as the vault.
const selectReportsAccount = acc => { emit('select-reports-account', acc); };
// Reports sub-nav (replaces the inline account list): Custom reports /
// Account performance / Agents performance. Routes via select-view, like Settings.
const goToReports = which => {
  const v = `reports-${which}`; // reports-custom | reports-accounts | reports-agents
  persistActiveItem(v);
  emit('select-view', v);
};
// Same accounts as the vault, but sorted by name for the Reports list.
const reportsAccounts = computed(() => [...fgAccounts.value].sort((a, b) => (a.name || '').localeCompare(b.name || '')));

// ============================================================================
// Close filter/sort dropdowns on outside click. (Fangate has no dropdowns
// anymore — the account list is inline.)
// ============================================================================

const closeAllDropdowns = () => {
  showFilterMenu.value   = false;
  showSortMenu.value     = false;
  showFilterMenuCh.value = false;
  showSortMenuCh.value   = false;
  searchDropdownOpen.value = false;
};

onMounted(()  => {
  document.addEventListener('click',  closeAllDropdowns);
});
onUnmounted(() => {
  document.removeEventListener('click', closeAllDropdowns);
  if (searchTimer) clearTimeout(searchTimer);
});
</script>

<template>
  <div class="fg-sidebar-root">

    <!-- ===== Search ===== -->
    <div class="fg-search-wrap">
      <div class="fg-search-inner">
        <svg class="fg-search-icon" width="14" height="14" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
        </svg>
        <input v-model="sbSearch" class="search-input" style="width:100%;box-sizing:border-box;"
               placeholder="Search chats, messages…"
               @click.stop @focus="reopenSearchIfResults" />

        <!-- Global-search results: Chats + message content (server full-text).
             Accounts are filtered in-place in the sections below. -->
        <div v-if="searchDropdownOpen" class="sb-search-dropdown" @click.stop>
          <div v-if="searchLoading" class="sb-search-state">Searching…</div>
          <template v-else>
            <div v-if="!searchConversations.length && !searchMessages.length"
                 class="sb-search-state">No chats or messages found</div>

            <div v-if="searchConversations.length" class="sb-search-group">
              <div class="sb-search-group-title">Chats</div>
              <button v-for="c in searchConversations" :key="'sc-' + c.id"
                      type="button" class="sb-search-row" @click="openSearchConversation(c)">
                <span class="sb-search-row-name">{{ c.contact?.name || ('Chat #' + c.id) }}</span>
                <span class="sb-search-row-meta">#{{ c.id }}<template v-if="c.inbox?.name"> · {{ c.inbox.name }}</template></span>
              </button>
            </div>

            <div v-if="searchMessages.length" class="sb-search-group">
              <div class="sb-search-group-title">Messages</div>
              <button v-for="m in searchMessages" :key="'sm-' + m.id"
                      type="button" class="sb-search-row" @click="openSearchMessage(m)">
                <span class="sb-search-row-name">{{ m.sender?.name || 'Message' }}</span>
                <span class="sb-search-row-snippet">{{ searchSnippet(m.content) }}</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ===== Nav ===== -->
    <nav class="fg-nav" id="sidebar-nav">

      <!-- ================================================================
           CHATS
           ================================================================ -->
      <div data-section="chats" style="position:relative;">
        <!-- Click on the CHATS section header: toggle the collapse AND
             switch the list pane to the multi-account overview (layer 0)
             so the agent gets the "all my accounts at a glance" view.
             Picking a specific account further down then drills into that
             account's chat list. -->
        <button class="nav-main" :class="{ open: open.chats }"
                @click="toggleSection('chats'); openAccountsOverview()"
                style="position:relative;">
          <span style="display:flex;align-items:center;gap:7px;">
            <span>Chats</span>
            <span v-if="unreadAccounts.length" class="pill-unread"
                  :title="unreadAccounts.length + ' account(s) with unread chats'">
              {{ unreadAccounts.length }}
            </span>
          </span>
          <svg class="chev" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>

        <!-- Filter dropdown — prototype: sb-filter-menu -->
        <div v-show="showFilterMenu" class="sb-dropdown-menu" @click.stop>
          <div class="sb-dropdown-header">Show accounts</div>
          <div>
            <div
              v-for="acc in allInboxItems" :key="acc.key"
              class="sb-filter-row"
              :style="{ opacity: isHidden(acc.key) ? 0.6 : 1 }"
              @click="toggleAccountVisibility(acc.key)"
            >
              <span class="sb-checkbox" :class="{ checked: !isHidden(acc.key) }">
                <svg v-if="!isHidden(acc.key)" width="8" height="8" viewBox="0 0 12 12"
                     fill="none" stroke="white" stroke-width="2.5">
                  <polyline points="2,6 5,9 10,3"/>
                </svg>
              </span>
              <span>{{ acc.name }}</span>
              <span v-if="acc.unread" class="pill-unread" style="margin-left:auto">
                {{ acc.unread }}
              </span>
            </div>
          </div>
          <!-- Filter accounts by assigned agent (inbox membership) -->
          <template v-if="isAccountAdmin">
            <div class="sb-dropdown-sep"></div>
            <div class="sb-dropdown-header">Filter by agent</div>
            <div v-if="!agents.length" class="sb-dropdown-muted">No agents</div>
            <div v-else>
              <div
                v-for="ag in agents" :key="'agf-' + ag.id"
                class="sb-filter-row"
                :style="{ opacity: sbActiveAgents.has(ag.id) ? 1 : 0.85 }"
                @click="toggleSbAgent(ag.id)"
              >
                <span class="sb-checkbox" :class="{ checked: sbActiveAgents.has(ag.id) }">
                  <svg v-if="sbActiveAgents.has(ag.id)" width="8" height="8" viewBox="0 0 12 12"
                       fill="none" stroke="white" stroke-width="2.5">
                    <polyline points="2,6 5,9 10,3"/>
                  </svg>
                </span>
                <span style="flex:1;">👤 {{ ag.name }}</span>
              </div>
            </div>
          </template>
          <!-- Filter accounts by label (accounts with ≥1 loaded chat carrying it) -->
          <template v-if="labels.length">
            <div class="sb-dropdown-sep"></div>
            <div class="sb-dropdown-header">Filter by label</div>
            <div
              v-for="lbl in labels" :key="'lblf-' + lbl.id"
              class="sb-filter-row"
              :style="{ opacity: sbActiveLabels.has(lbl.title) ? 1 : 0.85 }"
              @click="toggleSbLabel(lbl.title)"
            >
              <span class="sb-checkbox" :class="{ checked: sbActiveLabels.has(lbl.title) }">
                <svg v-if="sbActiveLabels.has(lbl.title)" width="8" height="8" viewBox="0 0 12 12"
                     fill="none" stroke="white" stroke-width="2.5">
                  <polyline points="2,6 5,9 10,3"/>
                </svg>
              </span>
              <span class="sb-label-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
              <span style="flex:1;">{{ lbl.title }}</span>
            </div>
          </template>
        </div>

        <!-- Sort dropdown — prototype: sb-sort-menu -->
        <div v-show="showSortMenu" class="sb-dropdown-menu" @click.stop>
          <div class="sb-dropdown-header">Sort</div>
          <div>
            <div
              v-for="opt in SB_SORT_OPTIONS" :key="opt.id"
              class="sb-sort-row"
              :class="{ active: sbAccountSort === opt.id }"
              @click="setSbSort(opt.id)"
            >
              <span class="sb-radio" :class="{ checked: sbAccountSort === opt.id }">
                <svg v-if="sbAccountSort === opt.id" width="6" height="6"
                     viewBox="0 0 10 10" fill="white">
                  <circle cx="5" cy="5" r="3"/>
                </svg>
              </span>
              {{ opt.label }}
            </div>
          </div>
        </div>

        <!-- Sub-content -->
        <div class="nav-sub-wrap" :class="{ open: open.chats }" data-sub="chats">
          <div class="nav-sub">

            <!-- Filter + Sort toolbar row -->
            <div class="fg-toolbar-row">
              <button
                class="fg-tool-btn"
                :class="{ 'fg-tool-btn--active': filterActive }"
                title="Filter accounts"
                @click="toggleFilter"
              >
                <svg width="12" height="12" viewBox="0 0 24 24"
                     style="display:block;flex-shrink:0;overflow:visible;"
                     :fill="'none'"
                     :stroke="filterActive ? '#0f766e' : '#6e7570'"
                     stroke-width="2.2" stroke-linecap="round">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="7" y1="12" x2="17" y2="12"/>
                  <line x1="10" y1="18" x2="14" y2="18"/>
                </svg>
              </button>
              <button
                class="fg-tool-btn"
                :class="{ 'fg-tool-btn--active': sortActive }"
                title="Sort accounts"
                @click="toggleSort"
              >
                <svg width="12" height="12" viewBox="0 0 24 24"
                     style="display:block;flex-shrink:0;overflow:visible;"
                     :fill="'none'"
                     :stroke="sortActive ? '#0f766e' : '#6e7570'"
                     stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="15" y2="12"/>
                  <line x1="3" y1="18" x2="9" y2="18"/>
                  <polyline points="17 15 21 18 17 21"/>
                </svg>
              </button>
            </div>

            <!-- Single flat account list — sorted by visibleAccounts
                 (unread band on top by activity DESC, then settled band).
                 Replaces the previous "Unread / All" two-subgroup split. -->
            <div class="nav-subgroup">
              <div class="nav-subgroup-items"
                   :class="{ 'sb-list-scroll': visibleAccounts.length > 6 }"
                   style="margin-left:0;padding:0;border-left:none;background:transparent;">
                <div v-if="visibleAccounts.length === 0"
                     class="nav-item" style="opacity:0.45;pointer-events:none;">
                  <span>No inboxes configured</span>
                </div>
                <div
                  v-for="acc in visibleAccounts" :key="'acc-' + acc.key"
                  class="nav-item"
                  :class="{
                    // Highlight ONLY the active account = the open chat's inbox.
                    // selectedInboxId is the single source of truth; the old
                    // `activeItem` clause (last-clicked account, sidebar-local)
                    // stayed lit after a search-jump opened a chat in a different
                    // account → two accounts highlighted. Mirrors CHANNELS below.
                    // (label view lights up the open chat's account the same way.)
                    active:
                      (view === 'chats' || view === 'label') &&
                      Number(selectedInboxId) === Number(acc.id),
                    'has-unread': acc.unread > 0,
                  }"
                  @click="setChatAccount(acc)"
                  @contextmenu.prevent.stop="onNavItemRightClick($event, acc)"
                >
                  <span class="nav-acc-label">
                    <svg class="nav-acc-tg" viewBox="0 0 240 240" aria-hidden="true">
                      <circle cx="120" cy="120" r="120" fill="#229ED9"/>
                      <path fill="#fff" d="M53.5 116.8c34.9-15.2 58.2-25.2 69.8-30 33.3-13.9 40.2-16.3 44.7-16.4 1 0 3.2.2 4.7 1.4.6.5 1 1.4 1.2 2.5.2 1.1.4 3.5.2 5.4-2.2 22.7-11.6 77.9-16.4 103.4-2 10.8-6 14.4-9.8 14.8-8.4.8-14.7-5.5-22.8-10.8-12.7-8.3-19.8-13.5-32.2-21.6-14.3-9.4-5-14.6 3.1-23 2.1-2.2 39.1-35.8 39.8-38.9.1-.4.2-1.8-.7-2.5-.9-.7-2.2-.5-3.1-.3-1.3.3-22.7 14.4-64 42.4-6 4.2-11.5 6.2-16.4 6.1-5.4-.1-15.8-3.1-23.5-5.6-9.5-3.1-17-4.7-16.4-9.9.3-2.7 4.1-5.5 11.4-8.5z"/>
                    </svg>
                    <span class="nav-acc-name">{{ acc.name }}</span>
                  </span>
                  <span v-if="acc.unread > 0" class="pill-unread">{{ acc.unread }}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- ================================================================
           CHANNELS
           Lists the same Telegram accounts as the Chats section.
           Clicking an account loads its channels into the list pane.
           ================================================================ -->
      <div data-section="channels" style="position:relative;">
        <button class="nav-main" :class="{ open: open.channels }"
                @click="toggleSection('channels'); openChannelsOverview()" style="position:relative;">
          <span style="display:flex;align-items:center;gap:7px;">
            <span>Channels &amp; Groups</span>
            <span v-if="unreadAccountsChannels.length" class="pill-unread"
                  :title="unreadAccountsChannels.length + ' account(s) with unread groups/channels'">
              {{ unreadAccountsChannels.length }}
            </span>
          </span>
          <!-- No "+ new" affordance here: you can't create a Telegram group or
               channel from Creator-Chat — those exist when the bot is added
               as a member / admin on Telegram's side. -->
          <svg class="chev" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>

        <!-- Channels filter dropdown (same global account filter as Chats) -->
        <div v-show="showFilterMenuCh" class="sb-dropdown-menu" @click.stop>
          <div class="sb-dropdown-header">Show accounts</div>
          <div>
            <div
              v-for="acc in allInboxItems" :key="'chf-' + acc.key"
              class="sb-filter-row"
              :style="{ opacity: isHidden(acc.key) ? 0.6 : 1 }"
              @click="toggleAccountVisibility(acc.key)"
            >
              <span class="sb-checkbox" :class="{ checked: !isHidden(acc.key) }">
                <svg v-if="!isHidden(acc.key)" width="8" height="8" viewBox="0 0 12 12"
                     fill="none" stroke="white" stroke-width="2.5">
                  <polyline points="2,6 5,9 10,3"/>
                </svg>
              </span>
              <span>{{ acc.name }}</span>
              <span v-if="acc.unreadChannels" class="pill-unread" style="margin-left:auto">
                {{ acc.unreadChannels }}
              </span>
            </div>
          </div>
          <template v-if="isAccountAdmin">
            <div class="sb-dropdown-sep"></div>
            <div class="sb-dropdown-header">Filter by agent</div>
            <div v-if="!agents.length" class="sb-dropdown-muted">No agents</div>
            <div v-else>
              <div
                v-for="ag in agents" :key="'chagf-' + ag.id"
                class="sb-filter-row"
                :style="{ opacity: sbActiveAgents.has(ag.id) ? 1 : 0.85 }"
                @click="toggleSbAgent(ag.id)"
              >
                <span class="sb-checkbox" :class="{ checked: sbActiveAgents.has(ag.id) }">
                  <svg v-if="sbActiveAgents.has(ag.id)" width="8" height="8" viewBox="0 0 12 12"
                       fill="none" stroke="white" stroke-width="2.5">
                    <polyline points="2,6 5,9 10,3"/>
                  </svg>
                </span>
                <span style="flex:1;">👤 {{ ag.name }}</span>
              </div>
            </div>
          </template>
          <template v-if="labels.length">
            <div class="sb-dropdown-sep"></div>
            <div class="sb-dropdown-header">Filter by label</div>
            <div
              v-for="lbl in labels" :key="'chlblf-' + lbl.id"
              class="sb-filter-row"
              :style="{ opacity: sbActiveLabels.has(lbl.title) ? 1 : 0.85 }"
              @click="toggleSbLabel(lbl.title)"
            >
              <span class="sb-checkbox" :class="{ checked: sbActiveLabels.has(lbl.title) }">
                <svg v-if="sbActiveLabels.has(lbl.title)" width="8" height="8" viewBox="0 0 12 12"
                     fill="none" stroke="white" stroke-width="2.5">
                  <polyline points="2,6 5,9 10,3"/>
                </svg>
              </span>
              <span class="sb-label-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
              <span style="flex:1;">{{ lbl.title }}</span>
            </div>
          </template>
        </div>

        <!-- Channels sort dropdown (shares the account sort with Chats) -->
        <div v-show="showSortMenuCh" class="sb-dropdown-menu" @click.stop>
          <div class="sb-dropdown-header">Sort</div>
          <div>
            <div
              v-for="opt in SB_SORT_OPTIONS" :key="'chs-' + opt.id"
              class="sb-sort-row"
              :class="{ active: sbAccountSort === opt.id }"
              @click="setSbSort(opt.id)"
            >
              <span class="sb-radio" :class="{ checked: sbAccountSort === opt.id }">
                <svg v-if="sbAccountSort === opt.id" width="6" height="6"
                     viewBox="0 0 10 10" fill="white">
                  <circle cx="5" cy="5" r="3"/>
                </svg>
              </span>
              {{ opt.label }}
            </div>
          </div>
        </div>

        <div class="nav-sub-wrap" :class="{ open: open.channels }" data-sub="channels">
          <div class="nav-sub">

            <!-- Filter + Sort toolbar row -->
            <div class="fg-toolbar-row">
              <button
                class="fg-tool-btn"
                :class="{ 'fg-tool-btn--active': filterActive }"
                title="Filter accounts"
                @click="toggleFilterCh"
              >
                <svg width="12" height="12" viewBox="0 0 24 24"
                     style="display:block;flex-shrink:0;overflow:visible;"
                     :fill="'none'"
                     :stroke="filterActive ? '#0f766e' : '#6e7570'"
                     stroke-width="2.2" stroke-linecap="round">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="7" y1="12" x2="17" y2="12"/>
                  <line x1="10" y1="18" x2="14" y2="18"/>
                </svg>
              </button>
              <button
                class="fg-tool-btn"
                :class="{ 'fg-tool-btn--active': sortActive }"
                title="Sort accounts"
                @click="toggleSortCh"
              >
                <svg width="12" height="12" viewBox="0 0 24 24"
                     style="display:block;flex-shrink:0;overflow:visible;"
                     :fill="'none'"
                     :stroke="sortActive ? '#0f766e' : '#6e7570'"
                     stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="15" y2="12"/>
                  <line x1="3" y1="18" x2="9" y2="18"/>
                  <polyline points="17 15 21 18 17 21"/>
                </svg>
              </button>
            </div>

            <div class="nav-subgroup">
              <div class="nav-subgroup-items"
                   :class="{ 'sb-list-scroll': visibleAccountsChannels.length > 6 }"
                   style="margin-left:0;padding:0;border-left:none;background:transparent;">
                <div v-if="visibleAccountsChannels.length === 0" class="nav-item" style="opacity:0.45;pointer-events:none;">
                  <span>No accounts configured</span>
                </div>
                <div
                  v-for="acc in visibleAccountsChannels" :key="'ch-' + acc.key"
                  class="nav-item"
                  :class="{
                    active: view === 'channels' && Number(selectedInboxId) === Number(acc.id),
                    'has-unread': acc.unreadChannels > 0,
                  }"
                  @click="setChannelAccount(acc)"
                  @contextmenu.prevent.stop="onNavItemRightClick($event, acc)"
                >
                  <span class="nav-acc-label">
                    <svg class="nav-acc-tg" viewBox="0 0 240 240" aria-hidden="true">
                      <circle cx="120" cy="120" r="120" fill="#229ED9"/>
                      <path fill="#fff" d="M53.5 116.8c34.9-15.2 58.2-25.2 69.8-30 33.3-13.9 40.2-16.3 44.7-16.4 1 0 3.2.2 4.7 1.4.6.5 1 1.4 1.2 2.5.2 1.1.4 3.5.2 5.4-2.2 22.7-11.6 77.9-16.4 103.4-2 10.8-6 14.4-9.8 14.8-8.4.8-14.7-5.5-22.8-10.8-12.7-8.3-19.8-13.5-32.2-21.6-14.3-9.4-5-14.6 3.1-23 2.1-2.2 39.1-35.8 39.8-38.9.1-.4.2-1.8-.7-2.5-.9-.7-2.2-.5-3.1-.3-1.3.3-22.7 14.4-64 42.4-6 4.2-11.5 6.2-16.4 6.1-5.4-.1-15.8-3.1-23.5-5.6-9.5-3.1-17-4.7-16.4-9.9.3-2.7 4.1-5.5 11.4-8.5z"/>
                    </svg>
                    <span class="nav-acc-name">{{ acc.name }}</span>
                  </span>
                  <span v-if="acc.unreadChannels > 0" class="pill-unread">{{ acc.unreadChannels }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================================================================
           LABELS — cross-account lens. Click a label to see every chat that
           carries it across ALL readable accounts (parent fetches server-side,
           so agents only get their own accounts' chats).
           ================================================================ -->
      <div data-section="labels">
        <button class="nav-main" :class="{ open: open.labels }"
                @click="toggleSection('labels'); openLabelsOverview()" style="position:relative;">
          <span>Labels</span>
          <svg class="chev" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>
        <div class="nav-sub-wrap" :class="{ open: open.labels }" data-sub="labels">
          <div class="nav-sub">
            <div class="nav-subgroup">
              <!-- Manage Labels — pinned ABOVE the (scrollable) label list so it
                   stays at the top no matter how many labels exist. Moved here
                   from Settings; visible to agents too (the page itself gates
                   create/edit/delete and swaps the copy by role). -->
              <div class="nav-item sb-label-item sb-manage-labels"
                   :class="{ active: view === 'settings-labels' }"
                   style="border-bottom:1px solid var(--border-soft);margin-bottom:4px;padding-bottom:7px;"
                   @click="goToSettings('labels')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style="opacity:0.7;flex-shrink:0;"
                     stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                <span class="sb-label-name">Manage Labels</span>
              </div>
              <div class="nav-subgroup-items"
                   :class="{ 'sb-list-scroll': labels.length > 6 }"
                   style="margin-left:0;padding:0;border-left:none;background:transparent;">
                <div v-if="!labels.length" class="nav-item" style="opacity:0.45;pointer-events:none;">
                  <span>No labels yet</span>
                </div>
                <div
                  v-for="lbl in labels" :key="'lbl-' + lbl.id"
                  class="nav-item sb-label-item"
                  :class="{ active: view === 'label' && selectedLabel === lbl.title }"
                  @click="selectLabel(lbl.title)"
                >
                  <span class="sb-label-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
                  <span class="sb-label-name">{{ lbl.title }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================================================================
           AGENTS — direct navigation, no sub-wrap
           ================================================================ -->
      <!-- Admin-only section. isAdmin comes from Chatwoot's useAdmin()
           composable — drives off getCurrentRole === 'administrator'. Hidden
           entirely for agents so the section literally doesn't exist for
           non-admins; the management page also gates server-side via
           Chatwoot's policy on /agents endpoints. -->
      <div v-if="isAccountAdmin" data-section="agents">
        <button
          class="nav-main"
          :class="{ open: view === 'agents' }"
          @click="activeItem = null; $emit('select-view', 'agents')"
        ><span>Agents</span></button>
      </div>

      <!-- ================================================================
           FANGATE — inline list of connected Fangate-vault inboxes.
           Each row opens the fullscreen vault for that inbox. The list is
           filtered to Fangate-connected inboxes only (token in localStorage);
           Chatwoot's inbox ACL already restricts the underlying inbox list to
           the current user, so agents naturally see only their assigned
           Fangate accounts.
           ================================================================ -->
      <div data-section="fangate">
        <button class="nav-main" :class="{ open: open.fangate }"
                @click="toggleSection('fangate'); activeItem = null; $emit('select-view', 'fangate')">
          <svg class="fg-vault-wordmark" viewBox="0 0 390 60" preserveAspectRatio="xMinYMid meet" role="img" aria-label="Fangate">
            <defs>
              <linearGradient id="fg-grad-sb" x1="0%" y1="50%" x2="100%" y2="50%">
                <stop offset="0%" stop-color="#1a0a6b"/>
                <stop offset="35%" stop-color="#3818b5"/>
                <stop offset="70%" stop-color="#5b2ecf"/>
                <stop offset="100%" stop-color="#7a3ce0"/>
              </linearGradient>
            </defs>
            <text x="0" y="48" font-family="Inter, system-ui, sans-serif" font-weight="900" font-size="56" letter-spacing="3.5" fill="url(#fg-grad-sb)">FANGATE.</text>
          </svg>
          <svg class="chev" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>
        <div class="nav-sub-wrap" :class="{ open: open.fangate }" data-sub="fangate">
          <div class="nav-sub">
            <div class="nav-subgroup">
              <div class="nav-subgroup-items"
                   :class="{ 'sb-list-scroll': fgAccounts.length > 6 }"
                   style="margin-left:0;padding:0;border-left:none;background:transparent;">
                <div v-if="!fgAccounts.length"
                     class="nav-item" style="opacity:0.45;pointer-events:none;">
                  <span>No Fangate accounts connected</span>
                </div>
                <div
                  v-for="acc in fgAccounts" :key="'fg-' + acc.key"
                  class="nav-item fg-nav-item"
                  :class="{ active: view === 'fangate-vault' && Number(selectedFangateInboxId) === Number(acc.id) }"
                  @click="selectFgAccount(acc)"
                  :title="acc.name"
                >
                  <span class="fg-nav-item-avatar">
                    <img v-if="acc.avatar" :src="acc.avatar" :alt="acc.name" />
                    <span v-else class="fg-nav-item-initials">{{ getInitials(acc.name) }}</span>
                  </span>
                  <span class="fg-nav-item-name">{{ acc.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================================================================
           REPORTS — visible to all. Agents see ONLY "Account performance"
           (scoped to their assigned accounts via the ACL-filtered list +
           the server inbox-assignment gate); Custom reports + Agents
           performance are admin-only (gated per-item below).
           ================================================================ -->
      <div data-section="reports">
        <button class="nav-main" :class="{ open: open.reports }"
                @click="toggleSection('reports'); activeItem = null">
          <span>Transactions &amp; (Live) Analytics</span>
          <svg class="chev" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>
        <div class="nav-sub-wrap" :class="{ open: open.reports }" data-sub="reports">
          <div class="nav-sub">
            <div class="nav-subgroup">
              <div class="nav-subgroup-items"
                   style="margin-left:0;padding:0;border-left:none;background:transparent;">
                <div v-if="isAccountAdmin" class="nav-item"
                     :class="{ active: view === 'reports-custom' }"
                     @click="goToReports('custom')">
                  <span>Custom reports</span>
                </div>
                <div class="nav-item"
                     :class="{ active: view === 'reports-accounts' || view === 'reports-transactions' }"
                     @click="goToReports('accounts')">
                  <span>Account performance</span>
                </div>
                <div v-if="isAccountAdmin" class="nav-item"
                     :class="{ active: view === 'reports-agents' }"
                     @click="goToReports('agents')">
                  <span>Agents performance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================================================================
           SETTINGS — visible to ALL. Agents see ONLY My Account + Help/Contact
           (they must be able to manage/delete their own account and log out);
           Chat-Accounts + Automations stay admin-only (gated per-item below).
           ================================================================ -->
      <div data-section="settings">
        <button class="nav-main" :class="{ open: open.settings }"
                @click="toggleSection('settings')">
          <span>Settings</span>
          <svg class="chev" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>
        <div class="nav-sub-wrap" :class="{ open: open.settings }" data-sub="settings">
          <div class="nav-sub">
            <div class="nav-subgroup">
              <div class="nav-subgroup-items"
                   style="max-height:600px;margin-left:0;padding:0;border-left:none;background:transparent;">
                <!-- Admin-only settings sub-pages. Agents must not see these.
                     Chat-Accounts = inbox config (admin domain); Bots & AI
                     = automation rules (admin domain). Labels + My Account
                     are reachable for everyone. -->
                <div v-if="isAccountAdmin" class="nav-item"
                     :class="{ active: view === 'settings-inboxes' }"
                     @click="goToSettings('inboxes')">
                  <span>Chat-Accounts</span>
                </div>
                <div v-if="isAccountAdmin" class="nav-item"
                     :class="{ active: view === 'settings-bots' }"
                     @click="goToSettings('bots')">
                  <span>Automations</span>
                </div>
                <!-- Manage Labels moved to the sidebar's LABELS section (pinned
                     at its top, visible to agents) — no longer a Settings item. -->
                <div class="nav-item"
                     :class="{ active: view === 'settings-account' }"
                     @click="goToSettings('account')">
                  <span>My Account</span>
                </div>
                <!-- Help / Contact — visible to admins AND agents (no gate). -->
                <div class="nav-item"
                     :class="{ active: view === 'settings-help' }"
                     @click="goToSettings('help')">
                  <span>Help/Contact</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </nav>

  </div>
</template>

<style lang="scss" scoped>
.fg-sidebar-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

// Fangate brand wordmark replacing the plain "Fangate Vault" section label
// (gradient FANGATE. — same asset as public/integrations/fangate/logo.svg).
.fg-vault-wordmark { height: 17px; width: auto; flex-shrink: 0; display: block; }

// ---------- Account-row dividers + unread emphasis (scoped) ----------------
// These rules were originally added in _fangate-theme.scss but global CSS
// updates didn't reliably propagate through Vite's HMR — moving them here
// guarantees they ship with FangateSidebar's compiled style chunk.
//
// Telegram mark + name in the CHATS account rows (own elements → scoped is fine).
.nav-acc-label { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; }
.nav-acc-tg { width: 14px; height: 14px; flex-shrink: 0; border-radius: 50%; }
.nav-acc-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

// `:deep()` is required because the .nav-item rows live inside this
// component's template but the scoped attribute prefix doesn't reach the
// shared `.nav-item` class — using :deep() lets us match them safely.
:deep(.nav-subgroup-items) {
  .nav-item + .nav-item {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 10px;
      right: 10px;
      height: 1px;
      background: rgba(74, 53, 32, 0.16);
      pointer-events: none;
    }
  }
  .nav-item.active + .nav-item::before,
  .nav-item.active::before,
  .nav-item + .nav-item.active::before {
    background: transparent !important;
  }
  // Active row: subtle teal outline so the selection reads as deliberate,
  // not just a tint. Box-shadow used instead of border so the row's
  // 5px border-radius corners stay perfectly rounded.
  .nav-item.active {
    box-shadow: inset 0 0 0 1.5px var(--accent) !important;
  }
  .nav-item.has-unread {
    color: var(--ink-strong) !important;
    font-weight: 700 !important;
    background: rgba(13, 148, 136, 0.08) !important;
  }
}

// ---- Search -----------------------------------------------------------------
.fg-search-wrap { padding: 16px 16px 12px; flex-shrink: 0; }
.fg-search-inner { position: relative; }
.fg-search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--ink-muted); pointer-events: none;
}

// ---- Global-search results dropdown -----------------------------------------
.sb-search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0; right: 0;
  z-index: 200;
  max-height: 360px;
  overflow-y: auto;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(31, 42, 42, 0.16);
  padding: 6px;
}
.sb-search-state {
  padding: 12px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  font-style: italic;
}
.sb-search-group { padding: 4px 0; }
.sb-search-group + .sb-search-group {
  border-top: 1px solid var(--border-soft);
  margin-top: 4px;
}
.sb-search-group-title {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted);
  padding: 4px 10px 6px;
}
.sb-search-row {
  display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
  width: 100%; text-align: left;
  padding: 7px 10px;
  border: none; background: transparent;
  border-radius: 8px; cursor: pointer;
  transition: background 0.1s;
  &:hover { background: var(--accent-tint); }
}
.sb-search-row-name {
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
  color: var(--ink-strong);
  max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sb-search-row-meta {
  font-family: 'Inter', sans-serif; font-size: 11px; color: var(--ink-muted);
}
.sb-search-row-snippet {
  font-family: 'Inter', sans-serif; font-size: 11.5px; color: var(--ink-medium);
  max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

// ---- LABELS section rows (color dot + name, left-aligned) -------------------
.sb-label-item {
  justify-content: flex-start;
  gap: 8px;
}
.sb-label-dot {
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
}
.sb-label-name {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

// ---- Nav scrollable ---------------------------------------------------------
.fg-nav {
  flex: 1; overflow-y: auto; padding: 0 12px 16px;
  display: flex; flex-direction: column; gap: 0;
}

// When an account list (Chats / Channels & Groups / Fangate Vault) has more than
// 6 entries, cap its height and give it its OWN scrollbar — so a long list doesn't
// push the other sidebar sections out of reach. ~6 rows tall (.nav-item ≈ 30px
// incl. divider).
.sb-list-scroll {
  max-height: 192px;
  overflow-y: auto;
  padding-right: 4px !important;   // keep the scrollbar off the unread pills
  // Slim, on-brand scrollbar
  scrollbar-width: thin;
  scrollbar-color: var(--border-medium) transparent;
  &::-webkit-scrollbar { width: 7px; }
  &::-webkit-scrollbar-thumb {
    background: var(--border-medium); border-radius: 4px;
  }
  &::-webkit-scrollbar-track { background: transparent; }
}


// ---- Filter / Sort dropdowns ------------------------------------------------
.sb-dropdown-menu {
  position: absolute; left: 0; right: 0; z-index: 100; margin-top: 2px;
  background: var(--bg-panel); border: 1px solid var(--border-soft);
  border-radius: 12px; box-shadow: 0 4px 20px rgba(31,42,42,0.13); padding: 8px;
}
.sb-dropdown-header {
  font-family: 'Inter', sans-serif; font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-muted);
  padding: 4px 8px 6px;
}
.sb-dropdown-sep { border-top: 1px solid var(--border-soft); margin: 5px 0 4px; }
.sb-dropdown-muted {
  font-family: 'Inter', sans-serif; font-size: 11px; color: var(--ink-muted);
  padding: 2px 10px 4px; font-style: italic;
}

.sb-filter-row {
  display: flex; align-items: center; gap: 8px; padding: 5px 10px;
  border-radius: 8px; cursor: pointer; font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 500; color: var(--ink-medium);
  transition: background 0.1s;
  &:hover { background: var(--bg-subtle); }
}
.sb-checkbox {
  width: 13px; height: 13px; border-radius: 3px; flex-shrink: 0;
  border: 1.5px solid var(--border-medium); background: transparent;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  &.checked { border-color: var(--accent); background: var(--accent); }
}

.sb-sort-row {
  display: flex; align-items: center; gap: 8px; padding: 6px 10px;
  border-radius: 8px; cursor: pointer; font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 500; color: var(--ink-medium);
  transition: background 0.1s;
  &.active { color: var(--accent-strong); background: var(--accent-tint); }
  &:not(.active):hover { background: var(--bg-subtle); }
}
.sb-radio {
  width: 13px; height: 13px; border-radius: 50%; flex-shrink: 0;
  border: 1.5px solid var(--border-medium); background: transparent;
  display: flex; align-items: center; justify-content: center;
  &.checked { border-color: var(--accent); background: var(--accent); }
}

// ---- Filter / Sort toolbar buttons -----------------------------------------
// Tightened: previously had a generous top/bottom that, combined with
// .nav-sub padding, made a noticeable gap between the CHATS header and
// the toolbar icons. Now flush to both edges so the toolbar reads as part
// of the section header band.
.fg-toolbar-row {
  display: flex; align-items: center; justify-content: flex-end; gap: 4px;
  padding: 2px 4px 3px;
  margin: 0 0 2px;
  border-bottom: 1px solid var(--border-soft);
}
.fg-tool-btn {
  display: inline-flex !important; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px;
  padding: 0 !important;
  border: 1px solid var(--border-soft); background: transparent;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: var(--accent-tint); border-color: var(--accent);
  }
  &--active {
    background: var(--accent-tint); border-color: var(--accent);
  }
}

// ---- Fangate account list (inline, replaces former dropdown selector) ------
// Each row shows a small avatar + the inbox name, styled to slot into the
// existing .nav-item rhythm (which renders just a span). Active row gets
// the same accent treatment as a selected chat-account row.
.fg-nav-item {
  display: flex; align-items: center; gap: 8px;
}
.fg-nav-item-avatar {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  background: var(--bg-subtle); border: 1.5px solid var(--border-soft);
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fg-nav-item-initials {
  font-family: 'Libre Baskerville', serif;
  font-size: 10px; font-weight: 700;
  color: var(--accent-strong); letter-spacing: 0.02em;
  line-height: 1;
}
.fg-nav-item-name {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}


// ---- "coming soon" badge ---------------------------------------------------
.coming-soon-badge {
  font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 600;
  color: var(--ink-muted); text-transform: none; letter-spacing: 0; margin-left: 4px;
}

</style>
